import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useAuth0 } from "@auth0/auth0-react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowUp,
  Check,
  ChevronDown,
  Loader2,
  Maximize2,
  Square,
  X,
  AlertCircle,
  Hourglass,
} from "lucide-react";
import { useAppSelector } from "@redux/hooks";
import { getArchiServiceUrl } from "@shared/config/featureFlags";
import { viewLensLabel } from "@shared/navigation/tabs/viewsModel";
import { archiAccess, askArchi } from "./api";
import { Suggestion, suggestionsFor } from "./suggestions";
import { ToolStep, toolLabel } from "./toolSteps";
import ArchiDiagram from "./ArchiDiagram";
import ArchiTimeline from "./ArchiTimeline";
import ArchiMark from "./ArchiMark";
import { DiagramSpec, splitAnswer } from "./diagramSpec";
import { nodeHref, useServiceLinks } from "./serviceLinks";
import { OrderDraft, answerNotes, draftOrder } from "./orderDraft";
import ChangeRequestModal from "settings/support/components/ChangeRequestModal";
import ArchiDock from "./ArchiDock";
import {
  Lens,
  Session,
  Turn,
  newId,
  titleOf,
  useArchiSessions,
} from "./sessions";

/**
 * Archi, rebuilt (P-CTX-123): a drawer on the right edge, over the page, that
 * answers about the network in view and draws it. It talks to the new Archi
 * service, which reads the Customer API with a short read-only pass held to
 * the user's own reach and to the lens on screen. Rendered only when that
 * service is configured (`getArchiServiceUrl`); otherwise the old sidebar stays.
 */

/** Wide tables scroll inside the answer instead of being clipped by the drawer. */
const MARKDOWN_COMPONENTS = {
  table: ({ children }: { children?: React.ReactNode }) => (
    <div className="my-3 overflow-x-auto rounded-xl border border-semantic-border-primary">
      <table>{children}</table>
    </div>
  ),
};

const ANSWER_STYLES =
  "text-[14px] leading-[1.65] text-semantic-text-primary [&_h1]:mb-2 [&_h1]:mt-4 [&_h1]:text-base [&_h1]:font-semibold [&_h2]:mb-2 [&_h2]:mt-4 [&_h2]:text-[15px] [&_h2]:font-semibold [&_h3]:mb-1 [&_h3]:mt-3 [&_h3]:font-semibold [&_hr]:my-4 [&_hr]:border-semantic-border-primary [&_li]:my-1 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:my-2 [&_strong]:font-semibold [&_ul]:list-disc [&_ul]:pl-5 [&_code]:rounded [&_code]:bg-semantic-bg-secondary [&_code]:px-1 [&_code]:text-[12px] [&_table]:w-full [&_table]:border-collapse [&_table]:text-[12px] [&_td]:border-t [&_td]:border-semantic-border-primary [&_td]:px-3 [&_td]:py-2 [&_td]:align-top [&_th]:bg-semantic-bg-secondary [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:font-semibold";

/**
 * What Archi read for this answer, folded into one line: the read in progress
 * while it works, "Read N sources" once done, the full list on demand.
 */
const Steps: React.FC<{ steps: ToolStep[]; working: boolean }> = ({
  steps,
  working,
}) => {
  const [open, setOpen] = useState(false);
  const running = steps.filter((s) => s.status === "running");
  const current = running[running.length - 1];
  const read = `Read ${steps.length} ${steps.length === 1 ? "source" : "sources"}`;
  // Between reads the model is thinking over what it has, not done.
  const summary = !working
    ? read
    : current
      ? `Reading ${toolLabel(current.name, current.label)}…`
      : `${read} · thinking…`;

  return (
    <div className="text-[13px]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex items-center gap-1.5 text-semantic-text-secondary hover:text-semantic-text-primary"
      >
        {working && (
          <ArchiMark size={18} working className="text-semantic-text-primary" />
        )}
        {working ? (
          <span className="bg-[linear-gradient(90deg,var(--color-semantic-text-secondary)_30%,var(--color-semantic-text-primary)_50%,var(--color-semantic-text-secondary)_70%)] bg-[length:200%_100%] bg-clip-text text-transparent motion-safe:animate-archi-shimmer">
            {summary}
          </span>
        ) : (
          <span>{summary}</span>
        )}
        <ChevronDown
          className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`}
          aria-hidden
        />
      </button>
      {open && (
        <ul className="mt-2 space-y-1.5 border-l border-semantic-border-primary pl-3">
          {steps.map((step) => (
            <li
              key={step.id}
              className="flex items-center gap-2 text-semantic-text-secondary motion-safe:animate-archi-rise"
            >
              {step.status === "running" && (
                <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
              )}
              {step.status === "done" && (
                <Check className="h-3 w-3" aria-hidden />
              )}
              {step.status === "error" && (
                <AlertCircle
                  className="h-3 w-3 text-semantic-text-danger"
                  aria-hidden
                />
              )}
              {toolLabel(step.name, step.label)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/** "Drawing…" while the diagram block is still streaming. */
const DrawingPlaceholder: React.FC = () => (
  <div className="flex h-24 items-center justify-center gap-2 rounded-2xl border border-dashed border-semantic-border-primary text-[13px] text-semantic-text-secondary motion-safe:animate-archi-rise">
    <ArchiMark size={18} working className="text-semantic-text-primary" />
    Drawing…
  </div>
);

const ModeTag: React.FC<{ spec: DiagramSpec }> = ({ spec }) =>
  spec.mode === "scenario" ? (
    <span className="rounded-full border border-semantic-border-brand-primary px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-semantic-text-brand">
      Proposed scenario
    </span>
  ) : (
    <span className="rounded-full bg-semantic-bg-secondary px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-semantic-text-secondary">
      Live network
    </span>
  );

const DiagramCard: React.FC<{
  spec: DiagramSpec;
  onOpen: (spec: DiagramSpec) => void;
}> = ({ spec, onOpen }) => (
  <figure className="overflow-hidden rounded-2xl border border-semantic-border-primary motion-safe:animate-archi-rise">
    <figcaption className="flex items-center gap-2 border-b border-semantic-border-primary px-3 py-2">
      <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-semantic-text-primary">
        {spec.title ?? "Diagram"}
      </span>
      <ModeTag spec={spec} />
      <button
        type="button"
        onClick={() => onOpen(spec)}
        aria-label="Open on the canvas"
        className="rounded-lg p-1 text-semantic-text-secondary hover:bg-semantic-bg-secondary hover:text-semantic-text-primary"
      >
        <Maximize2 className="h-3.5 w-3.5" aria-hidden />
      </button>
    </figcaption>
    <ArchiDiagram spec={spec} compact />
  </figure>
);

/**
 * Markdown renders no HTML, by design (an answer is not trusted markup), so a
 * `<br>` the model writes to break a table cell would show as text. It reads
 * as the separator it meant.
 */
const withoutLineBreakTags = (text: string) =>
  text.replace(/<br\s*\/?>/gi, " · ");

const AnswerText: React.FC<{ text: string }> = ({ text }) => (
  <div className={ANSWER_STYLES}>
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={MARKDOWN_COMPONENTS}>
      {withoutLineBreakTags(text)}
    </ReactMarkdown>
  </div>
);

const AssistantTurn: React.FC<{
  turn: Turn;
  /** Opens a drawing on the canvas, with the words of the answer it came in. */
  onOpenDiagram: (spec: DiagramSpec, notes: string) => void;
}> = ({ turn, onOpenDiagram }) => (
  <div className="space-y-3 motion-safe:animate-archi-rise">
    {turn.steps.length > 0 && (
      <Steps steps={turn.steps} working={turn.streaming} />
    )}
    {turn.streaming && !turn.text && turn.steps.length === 0 && (
      <div className="flex items-center gap-2 text-[13px] text-semantic-text-secondary">
        <ArchiMark size={18} working className="text-semantic-text-primary" />
        Thinking…
      </div>
    )}
    {splitAnswer(turn.text).map((part, i) => {
      if (part.kind === "text") return <AnswerText key={i} text={part.text} />;
      if (part.kind === "timeline")
        return part.spec ? (
          <ArchiTimeline key={i} spec={part.spec} />
        ) : (
          <DrawingPlaceholder key={i} />
        );
      if (!part.spec) return <DrawingPlaceholder key={i} />;
      return (
        <DiagramCard
          key={i}
          spec={part.spec}
          onOpen={(spec) => onOpenDiagram(spec, answerNotes(turn.text))}
        />
      );
    })}
    {turn.usageLimit && (
      <p className="flex items-start gap-2 rounded-xl bg-semantic-bg-secondary px-3 py-2.5 text-[13px] leading-relaxed text-semantic-text-primary">
        <Hourglass
          className="mt-0.5 h-3.5 w-3.5 shrink-0 text-semantic-text-secondary"
          aria-hidden
        />
        {turn.usageLimit.message}
      </p>
    )}
    {turn.error && (
      <p className="flex items-center gap-1.5 text-[13px] text-semantic-text-danger">
        <AlertCircle className="h-3.5 w-3.5" aria-hidden />
        {turn.error}
      </p>
    )}
  </div>
);

const UserTurn: React.FC<{ turn: Turn }> = ({ turn }) => (
  <div className="flex justify-end motion-safe:animate-archi-rise">
    <p className="max-w-[85%] whitespace-pre-wrap rounded-3xl bg-semantic-bg-secondary px-4 py-2.5 text-[14px] text-semantic-text-primary">
      {turn.text}
    </p>
  </div>
);

/** The question box: grows with the text, Enter sends, Shift+Enter breaks the line. */
const Composer: React.FC<{
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  onStop: () => void;
  busy: boolean;
  inputRef: React.RefObject<HTMLTextAreaElement>;
}> = ({ value, onChange, onSend, onStop, busy, inputRef }) => (
  <form
    onSubmit={(e) => {
      e.preventDefault();
      onSend();
    }}
    className="flex items-end gap-2 rounded-2xl border border-semantic-border-primary bg-semantic-bg-primary py-2 pl-4 pr-2 shadow-sm transition-colors focus-within:border-semantic-border-secondary"
  >
    <textarea
      ref={inputRef}
      value={value}
      onChange={(e) => {
        onChange(e.target.value);
        e.target.style.height = "auto";
        e.target.style.height = `${Math.min(e.target.scrollHeight, 160)}px`;
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          onSend();
        }
      }}
      rows={1}
      placeholder="Ask anything about your network…"
      aria-label="Message Archi"
      className="max-h-40 min-h-[24px] flex-1 resize-none self-center bg-transparent py-1 text-[14px] text-semantic-text-primary outline-none placeholder:text-semantic-text-secondary"
    />
    {busy ? (
      <button
        type="button"
        onClick={onStop}
        aria-label="Stop"
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-semantic-bg-inverse-primary text-semantic-text-inverse-primary"
      >
        <Square className="h-3 w-3 fill-current" />
      </button>
    ) : (
      <button
        type="submit"
        disabled={!value.trim()}
        aria-label="Send"
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-semantic-bg-inverse-primary text-semantic-text-inverse-primary transition-colors disabled:bg-semantic-bg-tertiary disabled:text-semantic-text-secondary"
      >
        <ArrowUp className="h-4 w-4" />
      </button>
    )}
  </form>
);

/**
 * Before the first question: who Archi is, what it is looking at, and four
 * ways to start. Sits at the top of the drawer; the question box stays docked
 * at the bottom, where it will be for the whole conversation.
 */
/**
 * A proposed scenario's next step, floating over the canvas: order it, or
 * tell Archi what to change. Ordering opens a change request — the way
 * services are ordered today — drafted from the scenario, for the user to
 * review and send.
 */
const ScenarioActions: React.FC<{
  onOrder: () => void;
  onModify: () => void;
}> = ({ onOrder, onModify }) => (
  <div className="pointer-events-none absolute inset-x-0 bottom-14 flex justify-center motion-safe:animate-archi-rise">
    <div className="pointer-events-auto flex items-center gap-2 rounded-2xl border border-semantic-border-primary bg-semantic-bg-primary p-1.5 pl-4 shadow-lg">
      <span className="mr-2 text-[13px] text-semantic-text-secondary">
        Like this proposal?
      </span>
      <button
        type="button"
        onClick={onModify}
        className="rounded-xl border border-semantic-border-primary px-3.5 py-2 text-[13px] font-semibold text-semantic-text-primary hover:bg-semantic-bg-secondary"
      >
        Modify proposal
      </button>
      <button
        type="button"
        onClick={onOrder}
        className="rounded-xl bg-semantic-bg-brand-bold-secondary px-3.5 py-2 text-[13px] font-semibold text-white hover:opacity-90"
      >
        Order now
      </button>
    </div>
  </div>
);

const EmptyState: React.FC<{
  onAsk: (q: string) => void;
  lens: string;
  suggestions: Suggestion[];
}> = ({
  onAsk,
  suggestions,
  lens,
}) => (
  <div className="flex flex-1 flex-col gap-6 overflow-y-auto px-5 pt-8 motion-safe:animate-archi-rise">
    <div className="space-y-2">
      <h2 className="font-heading text-[20px] font-semibold leading-tight text-semantic-text-primary">
        What do you want to know about your network?
      </h2>
      <p className="text-[13px] leading-relaxed text-semantic-text-secondary">
        Archi reads your live services and draws how they connect. It sees
        exactly what you see.
      </p>
      <p className="inline-flex items-center gap-1.5 rounded-full bg-semantic-bg-secondary px-2.5 py-1 text-[12px] text-semantic-text-secondary">
        <span
          className="h-1.5 w-1.5 rounded-full bg-semantic-bg-success-bold"
          aria-hidden
        />
        Looking at{" "}
        <span className="font-semibold text-semantic-text-primary">{lens}</span>
      </p>
    </div>
    <div className="grid grid-cols-2 gap-2">
      {suggestions.map(({ icon: Icon, text }, i) => (
        <button
          key={text}
          type="button"
          onClick={() => onAsk(text)}
          className="flex min-h-[84px] flex-col justify-between gap-3 rounded-xl border border-semantic-border-primary p-3 text-left text-[13px] leading-snug text-semantic-text-primary transition-colors hover:border-semantic-border-secondary hover:bg-semantic-bg-secondary motion-safe:animate-archi-rise"
          style={{ animationDelay: `${60 + i * 50}ms` }}
        >
          <Icon className="h-4 w-4 text-semantic-text-secondary" aria-hidden />
          {text}
        </button>
      ))}
    </div>
  </div>
);

/**
 * Whether to show Archi to this user at all; the Customer API decides (open to
 * everyone since 2026-10-28, CTX-1530). Asked once per sign-in; until the
 * answer comes, nothing shows, so a user it turns away never sees the button
 * flash in.
 */
function useArchiAccess(): boolean {
  const { getAccessTokenSilently, user } = useAuth0();
  const [allowed, setAllowed] = useState(false);
  useEffect(() => {
    const archiUrl = getArchiServiceUrl();
    if (!archiUrl || !user) return;
    const controller = new AbortController();
    getAccessTokenSilently()
      .then((token) =>
        archiAccess({ token, archiUrl, signal: controller.signal }),
      )
      .then(setAllowed)
      .catch(() => setAllowed(false));
    return () => controller.abort();
  }, [getAccessTokenSilently, user]);
  return allowed;
}

const ArchiPanel: React.FC = () => {
  const { getAccessTokenSilently, user } = useAuth0();
  const {
    sessions,
    active,
    activeId,
    setActiveId,
    create,
    update,
    rename,
    close,
    reopen,
    remove,
  } = useArchiSessions(user?.sub ?? "signed-out");
  /** Whether the active conversation's window is open; tabs stay either way. */
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  /** The drawing shown large on the canvas, beside the window. */
  const [canvas, setCanvasSpec] = useState<DiagramSpec | null>(null);
  /** What the answer said around the drawing: carried into an order's description. */
  const [canvasNotes, setCanvasNotes] = useState("");
  const setCanvas = useCallback((spec: DiagramSpec | null, notes = "") => {
    setCanvasSpec(spec);
    setCanvasNotes(notes);
  }, []);
  /** The change request "Order now" opened, prefilled from the scenario. */
  const [order, setOrder] = useState<OrderDraft | null>(null);

  const selection = useAppSelector((state) => state.selection);
  const ownTenantId = useAppSelector((state) => state.userProfile.tenantId);
  const tenantType = useAppSelector((state) => state.userProfile.TenantType);
  // The lens on screen now. Your own tenant is no lens at all: Archi then
  // reads everything you can see, as the dashboard does (for a global admin,
  // the whole platform).
  const currentLens: Lens = useMemo(() => {
    const tenantId =
      selection.tenantId && selection.tenantId !== ownTenantId
        ? selection.tenantId
        : null;
    return {
      tenantId,
      label:
        (tenantId && viewLensLabel(selection, tenantType)) ||
        "everything you can see",
    };
  }, [selection, ownTenantId, tenantType]);

  const location = useLocation();
  const navigate = useNavigate();
  // Read once a drawing is on the canvas, in the lens of the conversation it came from.
  const serviceLinks = useServiceLinks(
    active?.lensTenantId ?? ownTenantId ?? null,
    canvas !== null,
  );
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  /** One per conversation answering: closing or stopping one leaves the others running. */
  const aborts = useRef(new Map<string, AbortController>());
  /** Read inside a finishing answer, which outlives the render that started it. */
  const activeIdRef = useRef(activeId);
  activeIdRef.current = activeId;
  const turns = active?.turns ?? [];
  const busy = turns.some((t) => t.streaming);
  const allowed = useArchiAccess();

  // The conversation follows the lens on screen, under way or not: Archi
  // "sees exactly what you see", and a chat that kept the lens it started in
  // answered "not found" for a customer the user was looking at (2026-10-12).
  // Each question is sent with the conversation's lens, so it moves with it.
  useEffect(() => {
    if (!active) return;
    if (
      active.lensTenantId === currentLens.tenantId &&
      active.lensLabel === currentLens.label
    )
      return;
    update(active.id, (s) => ({
      ...s,
      lensTenantId: currentLens.tenantId,
      lensLabel: currentLens.label,
    }));
  }, [active, currentLens, update]);

  // Another conversation: its own drawings, not the last one's.
  useEffect(() => setCanvas(null), [activeId, setCanvas]);

  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [turns.length, activeId]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open, activeId]);

  const ask = useCallback(
    async (question: string) => {
      const text = question.trim();
      const archiUrl = getArchiServiceUrl();
      if (!text || busy || !archiUrl) return;
      // The conversation is fixed now: switching tabs mid-answer lets it finish in its own.
      const session: Pick<Session, "id" | "serverId" | "lensTenantId"> =
        active ?? {
          id: create(currentLens),
          serverId: null,
          lensTenantId: currentLens.tenantId,
        };
      setDraft("");
      if (inputRef.current) inputRef.current.style.height = "auto";
      const answerId = newId("t");
      update(session.id, (s) => ({
        ...s,
        title: s.renamed || s.turns.length > 0 ? s.title : titleOf(text),
        turns: [
          ...s.turns,
          { id: newId("t"), role: "user", text, steps: [], streaming: false },
          {
            id: answerId,
            role: "assistant",
            text: "",
            steps: [],
            streaming: true,
          },
        ],
      }));
      const patchAnswer = (patch: (t: Turn) => Turn) =>
        update(session.id, (s) => ({
          ...s,
          turns: s.turns.map((t) => (t.id === answerId ? patch(t) : t)),
        }));

      const controller = new AbortController();
      aborts.current.set(session.id, controller);
      try {
        const token = await getAccessTokenSilently();
        await askArchi({
          token,
          archiUrl,
          tenantId: session.lensTenantId,
          signal: controller.signal,
          body: {
            conversationId: session.serverId,
            message: text,
            currentRoute: location.pathname + location.search,
          },
          onEvent: (event, data) => {
            const d = (data ?? {}) as Record<string, unknown>;
            if (event === "meta" && typeof d.conversationId === "string") {
              const serverId = d.conversationId;
              update(session.id, (s) => ({ ...s, serverId }));
            } else if (event === "error" && d.code === "usage_limit") {
              patchAnswer((t) => ({
                ...t,
                usageLimit: {
                  message: String(d.message ?? ""),
                  resetsOn: typeof d.resetsOn === "string" ? d.resetsOn : null,
                },
              }));
            } else if (event === "error") {
              patchAnswer((t) => ({
                ...t,
                error: String(d.message ?? "Archi could not answer."),
              }));
            } else if (event === "text_delta") {
              const delta = String(d.text ?? "");
              // The model writes "let me check…" and the answer as one text; a read
              // between them is where one paragraph ends.
              patchAnswer((t) => ({
                ...t,
                text: t.text + (t.afterTool && t.text ? "\n\n" : "") + delta,
                afterTool: false,
              }));
            } else if (event === "tool_use") {
              patchAnswer((t) => ({
                ...t,
                steps: [
                  ...t.steps,
                  {
                    id: String(d.id),
                    name: String(d.name),
                    label: typeof d.label === "string" ? d.label : undefined,
                    status: "running",
                  },
                ],
                afterTool: true,
              }));
            } else if (event === "tool_result") {
              const isError = Boolean(d.isError);
              patchAnswer((t) => ({
                ...t,
                steps: t.steps.map((s) =>
                  s.id === String(d.id)
                    ? { ...s, status: isError ? "error" : "done" }
                    : s,
                ),
              }));
            }
          },
        });
        patchAnswer((t) => {
          // The first drawing of an answer opens on the canvas as soon as it is
          // complete — if its conversation is still the one on screen.
          const drawn = splitAnswer(t.text).find(
            (part) => part.kind === "diagram" && part.spec,
          );
          if (
            drawn &&
            drawn.kind === "diagram" &&
            drawn.spec &&
            activeIdRef.current === session.id
          )
            setCanvas(drawn.spec, answerNotes(t.text));
          return { ...t, streaming: false };
        });
      } catch (err) {
        const stopped = controller.signal.aborted;
        patchAnswer((t) => ({
          ...t,
          streaming: false,
          steps: t.steps.map((s) =>
            s.status === "running" ? { ...s, status: "error" } : s,
          ),
          error: stopped
            ? "Stopped."
            : err instanceof Error
              ? err.message
              : "Archi could not answer.",
        }));
      } finally {
        aborts.current.delete(session.id);
      }
    },
    [
      active,
      busy,
      create,
      currentLens,
      getAccessTokenSilently,
      location.pathname,
      location.search,
      setCanvas,
      update,
    ],
  );

  // REACT_APP_ARCHI_URL is the only switch, in every environment: set, this
  // panel replaces the old Archi; unset, the old one stays (AppLayout).
  if (!getArchiServiceUrl() || !allowed) return null;

  const startNew = () => {
    create(currentLens);
    setDraft("");
    setOpen(true);
  };
  const select = (id: string) => {
    // The open tab, clicked again, tucks its window away.
    if (id === activeId && open) return setOpen(false);
    setActiveId(id);
    setOpen(true);
  };
  const closeSession = (id: string) => {
    aborts.current.get(id)?.abort();
    if (id === activeId) setOpen(false);
    close(id);
  };
  const deleteSession = (id: string) => {
    aborts.current.get(id)?.abort();
    if (id === activeId) setOpen(false);
    remove(id);
  };

  return (
    <>
      <ArchiDock
        sessions={sessions}
        activeId={activeId}
        open={open}
        onSelect={select}
        onNew={startNew}
        onRename={rename}
        onClose={closeSession}
        onReopen={(id) => {
          reopen(id);
          setOpen(true);
        }}
        onDelete={deleteSession}
      />

      {open && canvas && (
        <section
          aria-label="Archi canvas"
          className="fixed bottom-16 left-3 right-[496px] top-3 z-50 flex flex-col overflow-hidden rounded-2xl border border-semantic-border-primary bg-semantic-bg-secondary shadow-2xl motion-safe:animate-archi-rise"
        >
          <header className="flex items-center gap-3 border-b border-semantic-border-primary bg-semantic-bg-primary px-4 py-3">
            <ArchiMark size={18} className="text-semantic-text-primary" />
            <p className="min-w-0 flex-1 truncate text-[14px] font-semibold text-semantic-text-primary">
              {canvas.title ?? "Diagram"}
            </p>
            <ModeTag spec={canvas} />
            <button
              type="button"
              onClick={() => setCanvas(null)}
              aria-label="Close canvas"
              className="rounded-lg p-1.5 text-semantic-text-secondary hover:bg-semantic-bg-secondary hover:text-semantic-text-primary"
            >
              <X className="h-4 w-4" />
            </button>
          </header>
          <div className="flex-1">
            <ArchiDiagram
              key={JSON.stringify(canvas)}
              spec={canvas}
              hrefOf={(node) => nodeHref(node, serviceLinks)}
              onOpen={(href) => {
                // The page opens under the chat, which stays; the canvas steps aside.
                setCanvas(null);
                navigate(href);
              }}
            />
          </div>
          {canvas.mode === "scenario" && (
            <ScenarioActions
              onOrder={() => setOrder(draftOrder(canvas, canvasNotes))}
              onModify={() => {
                setDraft("Change the proposal: ");
                inputRef.current?.focus();
              }}
            />
          )}
        </section>
      )}

      {/* Mounted only when ordering: the form loads topics and services as it mounts. */}
      {order && (
        <ChangeRequestModal
          isOpen
          onClose={() => setOrder(null)}
          prefill={order}
        />
      )}

      {open && active && (
        <section
          aria-label={`Archi · ${active.title}`}
          aria-busy={busy}
          className="fixed bottom-16 right-3 top-3 z-50 flex w-[480px] max-w-[calc(100vw-1.5rem)] flex-col overflow-hidden rounded-2xl border border-semantic-border-primary bg-semantic-bg-primary shadow-2xl motion-safe:animate-archi-in"
        >
          <header className="flex items-center gap-2 px-4 py-3">
            <ArchiMark
              size={18}
              className="shrink-0 text-semantic-text-primary"
            />
            <div className="min-w-0 flex-1">
              <p className="truncate font-heading text-[15px] font-semibold text-semantic-text-primary">
                {active.title}
              </p>
              <p className="truncate text-[11px] text-semantic-text-secondary">
                Looking at {active.lensLabel}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Minimize"
              className="rounded-lg p-1.5 text-semantic-text-secondary hover:bg-semantic-bg-secondary hover:text-semantic-text-primary"
            >
              <ChevronDown className="h-4 w-4" />
            </button>
          </header>

          {turns.length === 0 ? (
            <EmptyState
              onAsk={(q) => void ask(q)}
              lens={active.lensLabel}
              suggestions={suggestionsFor(location.pathname)}
            />
          ) : (
            <div
              ref={scrollRef}
              className="flex flex-1 flex-col gap-6 overflow-y-auto px-5 pb-4 pt-2"
            >
              {turns.map((turn) =>
                turn.role === "user" ? (
                  <UserTurn key={turn.id} turn={turn} />
                ) : (
                  <AssistantTurn
                    key={turn.id}
                    turn={turn}
                    onOpenDiagram={(spec, notes) => setCanvas(spec, notes)}
                  />
                ),
              )}
            </div>
          )}
          <div className="px-4 pb-3 pt-1">
            <Composer
              value={draft}
              onChange={setDraft}
              onSend={() => void ask(draft)}
              onStop={() => aborts.current.get(active.id)?.abort()}
              busy={busy}
              inputRef={inputRef}
            />
            <p className="mt-2 text-center text-[11px] text-semantic-text-secondary">
              Archi can be wrong. Check before you act.
            </p>
          </div>
        </section>
      )}
    </>
  );
};

export default ArchiPanel;
